import { createContext, useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import { refreshToken, logout } from "../services/authService";
import axiosInstance from "../services/axiosConfig";
import { toast } from "react-toastify";

export const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const storedUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("userData");
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.error("UserContext: Failed to parse stored user data:", error);
      return null;
    }
  }, []);
  const [user, setUserState] = useState(storedUser);
  const [loading, setLoading] = useState(!storedUser);
  const navigate = useNavigate();
  const location = useLocation();

  const hasAllowedRole = () => true;
  const updateUser = (userData) => {
    setUserState(userData);
    if (userData) {
      localStorage.setItem("userData", JSON.stringify(userData));
    } else {
      localStorage.removeItem("userData");
    }
  };
  const isOnSignInPage = () => window.location.pathname === "/authentication/sign-in";

  const refreshUser = useCallback(
    async (options = {}) => {
      const { showLoader = false } = options;
      if (showLoader) {
        setLoading(true);
      }
      try {
        const token = localStorage.getItem("authToken");
        const refreshTokenValue = localStorage.getItem("refreshToken");

        // Her iki token da yoksa direkt çık
        if (!token || !refreshTokenValue) {
          localStorage.removeItem("authToken");
          localStorage.removeItem("refreshToken");
          updateUser(null);
          return;
        }

        // Axios instance kullanarak kullanıcı bilgilerini al
        const response = await axiosInstance.get("users/me");
        if (hasAllowedRole(response.data?.roles || [])) {
          updateUser(response.data);
        } else {
          toast.error("Bu platforma erişim yetkiniz yok.", { position: "top-right" });
          localStorage.removeItem("authToken");
          localStorage.removeItem("refreshToken");
          updateUser(null);
          if (!isOnSignInPage()) {
            navigate("/authentication/sign-in", { replace: true });
          }
          return;
        }
      } catch (error) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          // Token geçersiz veya forbidden - refresh token dene
          try {
            await refreshToken();
            // Refresh başarılıysa kullanıcı bilgilerini tekrar al
            const userResponse = await axiosInstance.get("users/me");
            if (hasAllowedRole(userResponse.data?.roles || [])) {
              updateUser(userResponse.data);
            } else {
              toast.error("Bu platforma erişim yetkiniz yok.", { position: "top-right" });
              localStorage.removeItem("authToken");
              localStorage.removeItem("refreshToken");
              updateUser(null);
              if (!isOnSignInPage()) {
                navigate("/authentication/sign-in", { replace: true });
              }
              return;
            }
          } catch (refreshError) {
            // Refresh token da geçersizse güvenli logout
            localStorage.removeItem("authToken");
            localStorage.removeItem("refreshToken");
            updateUser(null);
            // Sadece sign-in sayfasında değilse yönlendir
            if (!isOnSignInPage()) {
              navigate("/authentication/sign-in", { replace: true });
            }
          }
        } else {
          // Diğer hatalar için sadece console'a log
          console.error("UserContext: User fetch error:", error);
        }
      } finally {
        if (showLoader) {
          setLoading(false);
        }
      }
    },
    [navigate]
  );

  const shouldShowInitialLoader = !storedUser;
  useEffect(() => {
    refreshUser({ showLoader: shouldShowInitialLoader });
  }, [refreshUser, shouldShowInitialLoader]); // Sadece component mount olduğunda çalışsın

  // Component'ler kendi redirect'lerini yapacak, burada yapmıyoruz

  // Global error handler - "No refresh token available" hatasını yakala
  useEffect(() => {
    const handleGlobalError = (event) => {
      if (
        event.error &&
        event.error.message &&
        event.error.message.includes("No refresh token available")
      ) {
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        updateUser(null);
        if (location.pathname !== "/authentication/sign-in") {
          navigate("/authentication/sign-in", { replace: true });
        }
      }
    };

    window.addEventListener("error", handleGlobalError);
    window.addEventListener("unhandledrejection", (event) => {
      if (
        event.reason &&
        event.reason.message &&
        event.reason.message.includes("No refresh token available")
      ) {
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        updateUser(null);
        if (location.pathname !== "/authentication/sign-in") {
          navigate("/authentication/sign-in", { replace: true });
        }
      }
    });

    return () => {
      window.removeEventListener("error", handleGlobalError);
    };
  }, []);

  const changePassword = async (oldPassword, newPassword, confirmPassword) => {
    try {
      const response = await axiosInstance.put("users/changePassword", {
        oldPassword,
        newPassword,
        confirmPassword,
      });

      return { success: true, data: response.data };
    } catch (error) {
      const status = error.response?.status;
      const payload = error.response?.data;
      let message =
        payload && typeof payload.message === "string" && payload.message.trim().length > 0
          ? payload.message
          : "";

      if (!message) {
        if (status === 400) {
          message = "Eski şifre yanlış";
        } else {
          message = "Parola değiştirilemedi.";
        }
      }

      const warnings = payload && Array.isArray(payload.warnings) ? payload.warnings : undefined;
      return { success: false, data: { message, warnings } };
    }
  };

  const handleLogout = () => {
    logout();
    updateUser(null);
    navigate("/authentication/sign-in", { replace: true });
  };

  return (
    <UserContext.Provider
      value={{
        user,
        setUser: updateUser,
        changePassword,
        logout: handleLogout,
        loading,
        refreshUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
